/**
 * Shared form input components for OnboardingWizard steps
 */
import { type ThemeObject, RADIUS } from "../theme.js";
import type { ChangeEvent } from "react";

// ── Input ──────────────────────────────────────────────
interface InputProps {
  label: string;
  value?: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  T: ThemeObject;
}

export function Input({ label, value, onChange, type = "text", placeholder = "", T }: InputProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: T.sub,
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: 0.7,
        }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: "9px 12px",
          borderRadius: RADIUS.sm,
          border: `1px solid ${T.border}`,
          background: T.bg,
          color: T.text,
          fontSize: 13,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

// ── Select ─────────────────────────────────────────────
interface SelectProps {
  label: string;
  value?: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: [string, string][];
  T: ThemeObject;
}

export function Select({ label, value, onChange, options, T }: SelectProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: T.sub,
          marginBottom: 4,
          textTransform: "uppercase",
          letterSpacing: 0.7,
        }}
      >
        {label}
      </label>
      <select
        value={value || ""}
        onChange={onChange}
        style={{
          width: "100%",
          padding: "9px 12px",
          borderRadius: RADIUS.sm,
          border: `1px solid ${T.border}`,
          background: T.bg,
          color: T.text,
          fontSize: 13,
        }}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Radio ──────────────────────────────────────────────
interface RadioProps {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description?: string;
  T: ThemeObject;
  isSelected?: boolean;
}

export function Radio({ name, checked, onChange, label, description, T, isSelected }: RadioProps) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 12px",
        borderRadius: RADIUS.sm,
        marginBottom: 6,
        cursor: "pointer",
        background: isSelected ? `${T.accent}14` : T.bg,
        border: `1px solid ${isSelected ? T.accent : T.border}`,
      }}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        style={{ marginTop: 3 }}
      />
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{label}</div>
        {description && <div style={{ fontSize: 11, color: T.sub }}>{description}</div>}
      </div>
    </label>
  );
}

// ── Checkbox ───────────────────────────────────────────
interface CheckboxProps {
  label: string;
  checked?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  T: ThemeObject;
  disabled?: boolean;
}

export function Checkbox({ label, checked, onChange, T, disabled }: CheckboxProps) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 0",
        borderBottom: `1px solid ${T.border}`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        style={{ width: 16, height: 16, cursor: disabled ? "not-allowed" : "pointer" }}
      />
      <span style={{ fontSize: 13, color: T.text }}>{label}</span>
    </label>
  );
}

// ── Badge ──────────────────────────────────────────────
interface BadgeProps {
  text: string;
  color: string;
}

export function Badge({ text, color }: BadgeProps) {
  return (
    <div
      style={{
        padding: "4px 10px",
        borderRadius: RADIUS.sm,
        background: `${color}20`,
        border: `1px solid ${color}60`,
        fontSize: 12,
        fontWeight: 600,
        color: color,
      }}
    >
      {text}
    </div>
  );
}
